/**
 * PaymentDetailsTable — the payments carried by the artifact (from
 * SearchPayments), rendered straight from the SearchPayments summaries.
 * Redux-connected to the searchPayments slice; each row's end-to-end id
 * links to its payment detail page.
 */
import { useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import StatusIndicator, { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import Table, { TableProps } from '@cloudscape-design/components/table';
import { useAppSelector } from 'src/store/hooks';
import { toFlowState, titleCase, fullTimestamp } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { ApiPaymentSummary } from 'src/store/slices/searchPayments/searchPaymentsApi';

const dash = (value?: string | null): string => (value && value !== 'null' ? value : '-');

const statusType = (state?: string): StatusIndicatorProps.Type => {
  const s = toFlowState(state);
  return s === 'SUCCESS' ? 'success' : s === 'FAILED' ? 'error' : 'in-progress';
};

export const PaymentDetailsTable = () => {
  const navigate = useNavigate();
  const { payments, total, loading, error } = useAppSelector(state => state.searchPayments);

  const columns: TableProps.ColumnDefinition<ApiPaymentSummary>[] = [
    {
      id: 'id',
      header: 'End-to-end id',
      cell: p => {
        const href = `/payment?id=${encodeURIComponent(p.id)}`;
        return (
          <Link href={href} onFollow={e => { e.preventDefault(); navigate(href); }}>{p.id}</Link>
        );
      },
    },
    { id: 'beaconStatus', header: 'Beacon status', cell: p => <StatusIndicator type={statusType(p.beaconPaymentState)}>{dash(p.beaconPaymentStatus)}</StatusIndicator> },
    { id: 'paymentStatus', header: 'Payment status', cell: p => dash(p.paymentDetails?.paymentStatus) },
    { id: 'system', header: 'System', cell: p => dash(p.currentSystem) },
    { id: 'direction', header: 'Direction', cell: p => (p.currentDirection ? titleCase(p.currentDirection) : '-') },
    { id: 'paymentMethod', header: 'Payment method', cell: p => dash(p.paymentDetails?.paymentMethod) },
    { id: 'bank', header: 'Bank', cell: p => dash(p.paymentDetails?.bank) },
    { id: 'updatedAt', header: 'Updated', cell: p => (p.updatedAt ? fullTimestamp(p.updatedAt) : '-') },
  ];

  return (
    <Table
      header={<Header counter={`(${total})`}>Payment details</Header>}
      columnDefinitions={columns}
      items={payments}
      trackBy="id"
      variant="container"
      loading={loading}
      loadingText="Searching payments…"
      resizableColumns
      empty={<Box textAlign="center" padding="s" color="text-body-secondary">{error ?? 'No payments found for this artifact'}</Box>}
    />
  );
};
